import { useLazyQuery } from '@apollo/client';
import { COUNTRY } from '@src/graphql/queries/country';
import { GET_SCHOOL_COUNTRY } from '@src/graphql/queries/school';
import { startTransition, useEffect, useMemo, useRef, useState, } from 'react';
export const useSchoolCountryManagement = ({ program, schoolPolicy, onProgramChange, otherText, defaultCountry, }) => {
    const [countrySelected, setCountrySelected] = useState(undefined);
    const initialCountryCode = useMemo(() => program.otherSchoolCountryCode || defaultCountry, [defaultCountry, program.otherSchoolCountryCode]);
    // Memoize country options
    const countryOptions = useMemo(() => {
        if (schoolPolicy?.countries) {
            const options = schoolPolicy.countries.map(country => ({
                value: country,
                label: country,
            }));
            return options;
        }
        else {
            return [
                {
                    value: defaultCountry,
                    label: defaultCountry,
                },
            ];
        }
    }, [schoolPolicy?.countries, defaultCountry]);
    // Store pending country updates to apply later without causing immediate re-render
    const pendingCountryUpdateRef = useRef(null);
    const pendingOtherCountryUpdateRef = useRef(null);
    const isUserCountryChangeRef = useRef(false);
    const lastFetchedSchoolNameRef = useRef(undefined);
    const schoolFetchTimeoutRef = useRef(null);
    // Fetch country data by code
    const [fetchCountry, { data: countryData }] = useLazyQuery(COUNTRY, {
        onCompleted: data => {
            if (data?.country) {
                setCountrySelected({
                    code: data.country.code,
                    id: data.country.id,
                    name: data.country.name || '',
                });
            }
        },
    });
    // Lazy query to look up a school by name and set its country (non-Other schools)
    const [fetchSchool] = useLazyQuery(GET_SCHOOL_COUNTRY, {
        fetchPolicy: 'cache-first',
        onCompleted: data => {
            const schoolCountry = data?.getSchoolCountry;
            if (!schoolCountry)
                return;
            if (program.schoolId === -1)
                return;
            const countryCode = schoolCountry.countryCode || '';
            const countryId = schoolCountry.countryId || 0;
            // Only update if values actually changed
            if (program.schoolCountryCode === countryCode &&
                (program.schoolCountryId || 0) === countryId) {
                return; // no change, don't do anything
            }
            // Store the update in a ref instead of applying immediately
            // This prevents the visible re-render while user is still focused on school field
            pendingCountryUpdateRef.current = { countryCode, countryId };
        },
    });
    // Fetch country once on mount using client default
    useEffect(() => {
        fetchCountry({ variables: { code: initialCountryCode } });
    }, [fetchCountry, initialCountryCode]);
    // Handle country change for "Other" schools
    const handleCountryChange = (country) => {
        isUserCountryChangeRef.current = true;
        // Apply any pending regular school country update (but not "Other" since user is changing it)
        if (pendingCountryUpdateRef.current) {
            const { countryCode, countryId } = pendingCountryUpdateRef.current;
            pendingCountryUpdateRef.current = null;
            startTransition(() => {
                onProgramChange(prg => ({
                    ...prg,
                    schoolCountryCode: countryCode,
                    schoolCountryId: countryId,
                }));
            });
        }
        if (!country) {
            // Handle empty country selection by clearing the fields
            onProgramChange(prg => ({
                ...prg,
                schoolCountryCode: undefined,
                schoolCountryId: undefined,
                otherSchoolCountryCode: undefined,
                otherSchoolCountryId: undefined,
            }));
            setCountrySelected(undefined);
            return;
        }
        fetchCountry({ variables: { code: country } });
    };
    // Effect to handle "Other" school country updates
    useEffect(() => {
        if (!countryData?.country)
            return;
        if (program.schoolId !== -1)
            return;
        const code = countryData.country.code;
        const idNum = Number(countryData.country.id);
        const isOtherEmpty = !program.otherSchoolCountryCode || program.otherSchoolCountryId == null;
        // Run only if
        // (a) Other school country code is empty (i.e on first page load) OR
        // (b) User changed country
        if (!(isOtherEmpty || isUserCountryChangeRef.current))
            return;
        // Only update if values actually changed
        if (program.otherSchoolCountryCode === code && (program.otherSchoolCountryId || 0) === idNum) {
            return; // no change
        }
        // Apply update immediately for "Other" schools so validation sees the value
        onProgramChange(prg => ({
            ...prg,
            schoolCountryCode: undefined,
            schoolCountryId: undefined,
            otherSchoolCountryCode: code,
            otherSchoolCountryId: idNum,
        }));
        isUserCountryChangeRef.current = false;
    }, [
        countryData?.country,
        program.schoolName,
        program.otherSchoolCountryCode,
        program.otherSchoolCountryId,
        otherText,
    ]);
    // Effect to fetch school country when school name changes
    useEffect(() => {
        const name = program.schoolName;
        if (!name || program.schoolId === -1)
            return;
        if (lastFetchedSchoolNameRef.current === name)
            return;
        lastFetchedSchoolNameRef.current = name;
        // Clear any pending timeout
        if (schoolFetchTimeoutRef.current) {
            clearTimeout(schoolFetchTimeoutRef.current);
        }
        // Delay the fetch by 300ms to ensure the dropdown has fully closed and rendered
        schoolFetchTimeoutRef.current = setTimeout(() => {
            fetchSchool({
                variables: {
                    name,
                },
                fetchPolicy: 'cache-first',
            });
        }, 300);
        return () => {
            if (schoolFetchTimeoutRef.current) {
                clearTimeout(schoolFetchTimeoutRef.current);
            }
        };
    }, [program.schoolName, fetchSchool]);
    // Apply pending country updates when user moves to another field
    const applyPendingCountryUpdate = () => {
        // Apply regular school country update
        if (pendingCountryUpdateRef.current) {
            const { countryCode, countryId } = pendingCountryUpdateRef.current;
            pendingCountryUpdateRef.current = null;
            // Use startTransition to make this update non-blocking
            startTransition(() => {
                onProgramChange(prg => ({
                    ...prg,
                    schoolCountryCode: countryCode,
                    schoolCountryId: countryId,
                }));
            });
        }
        // Apply "Other" school country update
        if (pendingOtherCountryUpdateRef.current) {
            const { countryCode, countryId } = pendingOtherCountryUpdateRef.current;
            pendingOtherCountryUpdateRef.current = null;
            // Use startTransition to make this update non-blocking
            startTransition(() => {
                onProgramChange(prg => ({
                    ...prg,
                    schoolCountryCode: undefined,
                    schoolCountryId: undefined,
                    otherSchoolCountryCode: countryCode,
                    otherSchoolCountryId: countryId,
                }));
            });
        }
    };
    return {
        countrySelected,
        countryOptions,
        handleCountryChange,
        applyPendingCountryUpdate,
    };
};
